import {Accordion, Col, Form, Row} from "react-bootstrap";
import {ADMIN_LANG} from "../../../../settings";
import RichTextField from "../../RichTextField";
import React from "react";
import {useAppDispatch, useAppSelector} from "../../../../app/store";
import {changeGameModal} from "../../../../app/slices/modals/gameEditModal";
import AsyncFormSelect from "../../gameView/AsyncFormSelect";
import {GameLanguages, PlainDetails} from "../../../../types/admin";
import {openModal as openFormModal} from "../../../../app/slices/modals/formModal";
import {AsyncOptions} from "../AsyncOptions";

type Props = {
    languageTab: string;
}

const Screens = ({languageTab}: Props) => {
    const dispatch = useAppDispatch();
    const {languages, form, end_form, form_options, game_id, display_end_screen_points} = useAppSelector((state) => state.gameEditModal);

    const handleDescriptionChange = (description: string) => {
        const updatedLanguages = languages.map((language) => {
            if (language.language_code === languageTab) {
                return {...language, description};
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
    }

    const handleEndDescriptionChange = (end_message: string) => {
        const updatedLanguages = languages.map((language: GameLanguages) => {
            if (language.language_code === languageTab) {
                return {...language, end_message};
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
    }

    const handleTermsChange = (terms: string) => {
        const updatedLanguages = languages.map((language: GameLanguages) => {
            if (language.language_code === languageTab) {
                return {...language, terms};
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
    }

    const handleEndFormThanksChange = (end_form_thanks: string) => {
        const updatedLanguages = languages.map((language: GameLanguages) => {
            if (language.language_code === languageTab) {
                return {...language, end_form_thanks};
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
    }

    const handleEndTitleChange = (end_title: string) => {
        const updatedLanguages = languages.map((language: GameLanguages) => {
            if (language.language_code === languageTab) {
                return {...language, end_title};
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
    }

    const handleRedirectChange = (end_redirect_url: string) => {
        const updatedLanguages = languages.map((language: GameLanguages) => {
            if (language.language_code === languageTab) {
                return {...language, end_redirect_url};
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
    }

    const handleFormChange = (form: PlainDetails, formType?: 'start' | 'end') => {
        const update = formType === 'start' ? { form } : { end_form: form };
        dispatch(changeGameModal(update));
    };

    const handleAddForm = (formType?: 'start' | 'end') => {
        if (game_id) {
            const formData = { form_id: null, game_id, game_form: formType === 'start' ? 'start' : 'end' };
            dispatch(openFormModal(formData));
        }
    };

    const handlePointShowChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeGameModal({display_end_screen_points: e.target.checked}));
    }

    const handleRemoveForm = (formType?: 'start' | 'end') => {
        const update = formType === 'start' ? { form: null } : { end_form: null };
        dispatch(changeGameModal(update));
    };

    const handleViewForm = (item: PlainDetails) => {
        if (game_id && item) {
            dispatch(openFormModal({ form_id: item.value, game_id }));
        }
    };

    return (
        <Accordion.Body>
            <Row className='gy-3'>
                <Col xs={12}>
                    <Form.Group controlId={`form_${languageTab}`}>
                        <Form.Label>{ADMIN_LANG.start_screen_form}</Form.Label>
                        <div className='d-flex align-items-center'>
                            <div className="flex-grow-1">
                                <AsyncFormSelect onChange={handleFormChange} initialValue={form}
                                                 defaultOptions={form_options} formType='start'/>
                            </div>
                            <AsyncOptions formType='start' item={form} handleAddNew={handleAddForm} handleViewItem={handleViewForm} handleRemoveSelection={handleRemoveForm}/>
                        </div>
                        <Form.Text className='help-text'>{ADMIN_LANG.start_screen_form_help}</Form.Text>
                    </Form.Group>
                </Col>
                <Col xs={12}>
                    <Form.Group controlId={`description_${languageTab}`}>
                        <Form.Label>{`${ADMIN_LANG.start_screen_text} (${languageTab.toUpperCase()})`}</Form.Label>
                        <RichTextField
                            value={languages.find((lang) => lang.language_code === languageTab)?.description || ''}
                            onChange={handleDescriptionChange}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12}>
                    <Form.Group controlId={`terms_${languageTab}`}>
                        <Form.Label>{`${ADMIN_LANG.terms} (${languageTab.toUpperCase()})`}</Form.Label>
                        <RichTextField
                            value={languages.find((lang) => lang.language_code === languageTab)?.terms || ''}
                            onChange={handleTermsChange}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12}>
                    <Form.Group controlId={`end_form_thanks_${languageTab}`}>
                        <Form.Label>{`${ADMIN_LANG.end_form_thanks} (${languageTab.toUpperCase()})`}</Form.Label>
                        <RichTextField
                            value={languages.find((lang) => lang.language_code === languageTab)?.end_form_thanks || ''}
                            onChange={handleEndFormThanksChange}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12}>
                    <Form.Group controlId={`form_${languageTab}`}>
                        <Form.Label>{ADMIN_LANG.end_screen_form}</Form.Label>
                        <div className='d-flex align-items-center'>
                            <div className="flex-grow-1">
                                <AsyncFormSelect onChange={handleFormChange} initialValue={end_form}
                                                 defaultOptions={form_options} formType='end'/>
                            </div>
                            <AsyncOptions formType='end' item={end_form} handleAddNew={handleAddForm} handleViewItem={handleViewForm} handleRemoveSelection={handleRemoveForm}/>
                        </div>
                        <Form.Text className='help-text'>{ADMIN_LANG.end_screen_form_help}</Form.Text>
                    </Form.Group>
                </Col>
                <Col xs={6}>
                    <Form.Group controlId={`end_screen_title_${languageTab}`}>
                        <Form.Label>{`${ADMIN_LANG.end_title} (${languageTab.toUpperCase()})`}</Form.Label>
                        <Form.Control type='text'
                                      maxLength={255}
                                      value={languages.find((lang: GameLanguages) => lang.language_code === languageTab)?.end_title || ''}
                                      onChange={(e) => handleEndTitleChange(e.target.value)}/>
                    </Form.Group>
                </Col>
                <Col xs={6}>
                    <Form.Group controlId={`redirect_${languageTab}`}>
                        <Form.Label>{`${ADMIN_LANG.game_end_redirect} (${languageTab.toUpperCase()})`}</Form.Label>
                        <Form.Control type='text'
                                      maxLength={255}
                                      value={languages.find((lang: GameLanguages) => lang.language_code === languageTab)?.end_redirect_url || '/'}
                                      onChange={(e) => handleRedirectChange(e.target.value)}/>
                    </Form.Group>
                </Col>
                <Col xs={12}>
                    <Form.Group controlId={`display_end_screen_points_${languageTab}`}>
                        <Form.Switch
                            className='form-label'
                            onChange={handlePointShowChange}
                            checked={display_end_screen_points}
                            label={ADMIN_LANG.display_end_screen_points}
                        />
                        <Form.Text className='help-text'>{ADMIN_LANG.display_end_screen_points_help}</Form.Text>
                    </Form.Group>
                </Col>
                <Col xs={12}>
                    <Form.Group controlId={`end_screen_text_${languageTab}`}>
                        <Form.Label>{`${ADMIN_LANG.end_screen_text} (${languageTab.toUpperCase()})`}</Form.Label>
                        <RichTextField
                            value={languages.find((lang: GameLanguages) => lang.language_code === languageTab)?.end_message || ''}
                            onChange={handleEndDescriptionChange}
                        />
                    </Form.Group>
                </Col>

            </Row>
        </Accordion.Body>
    )
}

export default Screens;
